import {
  ColumnDirective,
  ColumnsDirective,
  GridComponent,
  Inject,
  Resize,
  Sort,
} from "@syncfusion/ej2-react-grids";
import axios from "axios";
import { getCookie } from "cookies-next";
import { useCallback, useEffect, useRef, useState } from "react";
import { BiChevronLeft, BiChevronRight } from "react-icons/bi";
import { FaEye } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { Header, PageLoading } from "../../../components";
import { API_ROOT } from "../../../config";
import "../../table.css";

const LIMIT_OPTIONS = [10, 25, 50, 100];

const DocumentSignatures = () => {
  const navigate = useNavigate();
  const gridRef = useRef(null);
  const abortRef = useRef(null);

  const [pageLoading, setPageLoading] = useState(true);
  const [gridLoading, setGridLoading] = useState(false);
  const [documentSignatures, setDocumentSignatures] = useState([]);

  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [currentLimit, setCurrentLimit] = useState(10);

  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const fetchData = useCallback(
    async (page, limit, search) => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setGridLoading(true);
      try {
        const response = await axios.get(
          API_ROOT + "/admin/document-signature/list",
          {
            signal: controller.signal,
            headers: { Authorization: getCookie("admin_auth") },
            params: {
              page,
              limit,
              search: search || undefined,
            },
          },
        );

        const resData = response.data.data;
        const rows = resData?.data ?? [];
        const rawPg = resData?.pagination ?? {};

        const pg = {
          total: rawPg.total ?? 0,
          page: rawPg.page ?? page,
          limit: rawPg.limit ?? limit,
          totalPages:
            rawPg.totalPages ??
            Math.max(Math.ceil((rawPg.total ?? 0) / (rawPg.limit ?? limit)), 1),
        };

        setDocumentSignatures(
          rows.map((item, index) => ({
            No: (pg.page - 1) * pg.limit + index + 1,
            Uuid: item.uuid,
            Title: item.title,
            NameSigner: item.user?.name ?? "-",
            Status: item.status,
            RegistrationNumber: item.registration_number,
            PaymentMethod: item.payment_method?.payment_method_name,
            SerialNumber: item.serial_number,
            SignAt: item.document_recipients?.sign_at,
            FileMarkany: item.file_markany,
            BadanUsahaName: item.badan_usaha?.name || "-",
          })),
        );
        setPagination(pg);
      } catch (error) {
        if (error.code === "ERR_CANCELED" || axios.isCancel(error)) return;
        if (error.response?.status === 401) return navigate("/login");
        toast.error("Failed to load document signature data");
      } finally {
        setPageLoading(false);
        setGridLoading(false);
      }
    },
    [navigate],
  );

  useEffect(() => {
    fetchData(currentPage, currentLimit, appliedSearch);
  }, [currentPage, currentLimit, appliedSearch, fetchData]);

  const handleSearch = () => {
    setAppliedSearch(searchInput.trim());
    setCurrentPage(1);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleLimitChange = (e) => {
    setCurrentLimit(Number(e.target.value));
    setCurrentPage(1);
  };

  const queryCellInfo = (args) => {
    args.cell?.classList.add("e-cell");
  };

  const dataBound = () => {
    if (gridRef.current) {
      gridRef.current.autoFitColumns([
        "No",
        "Title",
        "NameSigner",
        "Status",
        "RegistrationNumber",
        "PaymentMethod",
        "BadanUsahaName",
        "SignAt",
        "Action",
      ]);
    }
  };

  const statusTemplate = (props) => (
    <span className="px-2 py-1 rounded-full text-xs uppercase font-medium bg-blue-100 text-blue-800">
      {props.Status}
    </span>
  );

  const formatDateTime = (value) => {
    if (!value) return "-";
    try {
      return new Date(value).toLocaleString();
    } catch (e) {
      return value;
    }
  };

  const createdAtTemplate = (props) => (
    <div className="text-xs">{formatDateTime(props.SignAt)}</div>
  );

  const actionTemplate = (props) => {
    return (
      <div className="e-actionParent flex gap-2">
        <button
          className="e-action"
          onClick={() => {
            const base =
              `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_API_VERSION}` ||
              "";
            const serial = props?.SerialNumber;
            const token = getCookie("admin_auth");
            const url = `${base.replace(/\/$/, "")}/admin/document-signature/document/${serial}?token=${token}`;
            window.open(url, "_blank", "noopener,noreferrer");
          }}
        >
          <FaEye className="e-actionIcon" />
          <div className="text-nowrap">Lihat File</div>
        </button>

        {props.FileMarkany && (
          <button
            className="flex items-center gap-2 py-2 px-3 border border-[#3b82f6] rounded-md text-[#3b82f6]"
            onClick={() => {
              window.open(props.FileMarkany, "_blank", "noopener,noreferrer");
            }}
          >
            <FaEye className="text-lg" />
            <div className="text-nowrap">File Markany</div>
          </button>
        )}
      </div>
    );
  };

  const { page, totalPages, total } = pagination;

  return pageLoading ? (
    <PageLoading />
  ) : (
    <div className="m-2 md:m-10 mt-24 px-2 py-10 md:p-10 bg-white rounded-3xl">
      <Header title="Document Signature" />
      <div className="flex flex-col gap-4">
        <hr />

        <div className="flex mb-2 gap-4 items-end">
          <div className="flex-1 max-w-sm">
            <label className="block text-sm mb-1">Cari</label>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Cari title, no registrasi, serial number..."
              className="block border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            className="text-white w-44 py-2 px-4 rounded-md text-sm bg-green-500 hover:bg-green-600"
            onClick={handleSearch}
          >
            Filter
          </button>
        </div>

        <div className="overflow-x-auto relative">
          {gridLoading && (
            <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center">
              Loading...
            </div>
          )}
          <div className="w-full">
            <GridComponent
              dataSource={documentSignatures}
              width="auto"
              allowSorting
              allowTextWrap={true}
              textWrapSettings={{ wrapMode: "Content" }}
              queryCellInfo={queryCellInfo}
              allowExcelExport={true}
              dataBound={dataBound}
              ref={gridRef}
            >
              <ColumnsDirective>
                <ColumnDirective
                  field="No"
                  headerText="No"
                  textAlign="Center"
                  width={50}
                />
                <ColumnDirective
                  field="Title"
                  headerText="Title"
                  textAlign="Left"
                  minWidth={150}
                  maxWidth={300}
                  textWrap={true}
                />
                <ColumnDirective
                  field="NameSigner"
                  headerText="Name Signer"
                  textAlign="Left"
                  minWidth="150"
                />
                <ColumnDirective
                  field="BadanUsahaName"
                  headerText="Badan Usaha Signer"
                  textAlign="Left"
                  minWidth="150"
                />
                <ColumnDirective
                  field="Status"
                  headerText="Status"
                  textAlign="Center"
                  template={statusTemplate}
                  minWidth={100}
                />
                <ColumnDirective
                  field="RegistrationNumber"
                  headerText="Registration Number"
                  textAlign="Left"
                  minWidth={150}
                />
                <ColumnDirective
                  field="PaymentMethod"
                  headerText="Payment Method"
                  textAlign="Left"
                  minWidth={150}
                />
                <ColumnDirective
                  field="SignAt"
                  headerText="Signed At"
                  textAlign="Left"
                  template={createdAtTemplate}
                  minWidth={180}
                />
                <ColumnDirective
                  field="Action"
                  headerText="Action"
                  textAlign="Center"
                  isPrimaryKey={true}
                  template={actionTemplate}
                  minWidth={450}
                />
              </ColumnsDirective>
              <Inject services={[Sort, Resize]} />
            </GridComponent>
          </div>
        </div>

        <div className="flex items-center justify-between mt-2 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <span>Tampilkan</span>
            <select
              value={currentLimit}
              onChange={handleLimitChange}
              className="border border-gray-300 rounded px-2 py-1"
            >
              {LIMIT_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            <span>
              data per halaman · Total <strong>{total}</strong> data
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-40"
            >
              <BiChevronLeft size={20} />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(
                (p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1,
              )
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                acc.push(p);
                return acc;
              }, [])
              .map((item, idx) =>
                item === "..." ? (
                  <span key={`e-${idx}`} className="px-2">
                    …
                  </span>
                ) : (
                  <button
                    key={item}
                    onClick={() => setCurrentPage(item)}
                    className={`w-8 h-8 rounded text-sm ${
                      item === page
                        ? "bg-green-500 text-white"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    {item}
                  </button>
                ),
              )}
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-40"
            >
              <BiChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentSignatures;
